import React from 'react';
import { unescapeNotesHtml } from '@helpers/htmlUtils';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import RedFlagPathologyFormItem from '@components/Clinicians/Session/Components/RedFlagPathologyFormItem';
import ComplexCaseFormItem from '@components/Clinicians/Session/Components/ComplexCaseFormItem';
import DiagnosisFormItem from '@components/Clinicians/Session/Components/DiagnosisFormItem';
import ImprovementStatusFormItem from '@components/Clinicians/Session/Components/ImprovementStatusFormItem';
import PresentingConditionFormItem from '@components/Clinicians/Session/Components/PresentingConditionFormItem';
import AgreedPlanFormItem from '@components/Clinicians/Session/Components/AgreedPlanFormItem';
import WorkingDiagnosisFormItem from '@components/Clinicians/Session/Components/WorkingDiagnosisFormItem';
import AdditionalNotesFormItem from '@components/Clinicians/Session/Components/AdditionalNotesFormItem';
import FactsheetsFormItem from '@components/Clinicians/Session/Components/FactsheetsFormItem';
import IntClinicalNotesFormItem from '@components/Clinicians/Session/Components/IntClinicalNotesFormItem';
import RecommendationNotes from '@components/Clinicians/Session/Components/RecommendationNotes';
import { css } from '@emotion/react';
import { Collapse } from 'antd';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionFormState } from '@hooks/useSessionFormState';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import UpdateNotesFormItem from '@components/Clinicians/Session/Components/UpdateNotesFormItem';
import Datestamp from 'vl-common/src/components/Datestamp';
import styled from '@emotion/styled';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import { PreviousAppointmentNotes } from '../PatientInformation/AllPreviousAppointmentNotes';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import CollapsibleQuickNotes from '@components/Clinicians/Session/Components/CollapsibleQuickNotes';
import CollapsibleTranscript from '@components/Clinicians/Session/Components/CollapsibleTranscript';
import ClinicalTabs, { type ClinicalTabItem } from '@components/Clinicians/Session/Components/ClinicalTabs';

export const useClinicalSavedValues = () => {
  const appointment_id = useSessionAppointmentId();
  const {
    appointment: { red_flag_pathology, complex_case }
  } = useSessionAppointment();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);
  const allAppointmentNotes = Object.fromEntries(
    (detailsCurrentAppointment?.notes || []).map(({ note_code, note_text }) => [note_code, note_text])
  );

  const initialValues = detailsCurrentAppointment?.recnotes?.map(({ recnote_code }) => recnote_code) || [];

  return {
    red_flag: red_flag_pathology || false,
    complex_case: complex_case || false,
    QUICK: allAppointmentNotes?.QUICK || '',
    IMPRO: allAppointmentNotes?.IMPRO || '',
    HPC: unescapeNotesHtml(allAppointmentNotes.HPC || ''),
    PLAN: unescapeNotesHtml(allAppointmentNotes.PLAN || ''),
    WD: unescapeNotesHtml(allAppointmentNotes.WD || ''),
    'D-COM': unescapeNotesHtml(allAppointmentNotes['D-COM'] || ''),
    INT: unescapeNotesHtml(allAppointmentNotes.INT || ''),
    UPDATE: unescapeNotesHtml(allAppointmentNotes.UPDATE || ''),
    RECOMMENDATIONS: initialValues
  };
};

const clinicalNotesComponentMap = {
  DIAGNOSIS: <DiagnosisFormItem />,
  HPC: <PresentingConditionFormItem />,
  IMPRO: <ImprovementStatusFormItem />,
  PLAN: <AgreedPlanFormItem />,
  WD: <WorkingDiagnosisFormItem />,
  'D-COM': <AdditionalNotesFormItem />,
  FACTSHEET: <FactsheetsFormItem />,
  INT: <IntClinicalNotesFormItem />,
  UPDATE: <UpdateNotesFormItem />,
  RECOMMENDATIONS: <RecommendationNotes />
};

const Pad = styled.div`
  padding: 0 1em 1em 1em;
`;

const Clinical = () => {
  const appointment_id = useSessionAppointmentId();

  const { data: appointmentsMeta } = useAppointmentMeta();
  const showAiNotes = useHeidiEnabled(appointment_id);
  const showComplexCheckbox = appointmentsMeta?.appt_complex_flags || false;
  const showRedFlagCheckbox = appointmentsMeta?.appt_red_flags || false;

  const { detailsPastCompletedAppointments } = useAppointmentDetails(appointment_id);

  const { Panel } = Collapse;

  const {
    'D-COM': DCOM,
    HPC,
    WD,
    PLAN,
    QUICK,
    IMPRO,
    INT,
    UPDATE,
    complex_case,
    red_flag,
    RECOMMENDATIONS
  } = useClinicalSavedValues();
  useSessionFormState('QUICK', QUICK);
  useSessionFormState('WD', WD);
  useSessionFormState('HPC', HPC);
  useSessionFormState('D-COM', DCOM);
  useSessionFormState('PLAN', PLAN);
  useSessionFormState('IMPRO', IMPRO);
  useSessionFormState('INT', INT);
  useSessionFormState('UPDATE', UPDATE);
  useSessionFormState('complex_case', complex_case);
  useSessionFormState('red_flag', red_flag);
  useSessionFormState('RECOMMENDATIONS', RECOMMENDATIONS);

  const { notesOrder } = useClinicalNotesMeta();

  const hasPastAppointments = detailsPastCompletedAppointments?.length > 0;

  const clinicalTab: ClinicalTabItem[] = [
    {
      label: 'Current Appointment',
      key: '0',
      children: (
        <Pad
          css={css`
            & > div {
              margin-bottom: 1em;

              .ant-form-item {
                margin-bottom: 0;
              }
            }
          `}
        >
          {showAiNotes && <CollapsibleQuickNotes />}
          {showAiNotes && <CollapsibleTranscript />}
          {showRedFlagCheckbox && <RedFlagPathologyFormItem />}
          {showComplexCheckbox && <ComplexCaseFormItem />}
          {notesOrder.map((noteCode) => clinicalNotesComponentMap[noteCode] || null)}
        </Pad>
      )
    },
    {
      label: `Previous Appointments (${detailsPastCompletedAppointments?.length})`,
      key: '1',
      focusablePanel: !hasPastAppointments,
      children: (
        <Pad>
          {hasPastAppointments ? (
            <Collapse bordered={false} defaultActiveKey={['0']}>
              {detailsPastCompletedAppointments.map((past_appt) => (
                <Panel
                  key={past_appt?.appt_id}
                  header={
                    <h3>
                      <Datestamp
                        date={new Date(past_appt?.appt_date || past_appt?.appt_completed_date || Date())}
                        format="Date @ Time (long, 24h) - Friday, March 3rd, 2022 @ 08:57"
                      />
                    </h3>
                  }
                  style={{ backgroundColor: 'white' }}
                >
                  <PreviousAppointmentNotes key={past_appt?.appt_id} appointment_info={past_appt} />
                </Panel>
              ))}
            </Collapse>
          ) : (
            'No previous appointment notes'
          )}
        </Pad>
      )
    }
  ];

  return <ClinicalTabs defaultActiveKey="0" items={clinicalTab} />;
};

export default Clinical;
